/**
 * StatusSelect: E1: the customer status control (client page header + grid row).
 *
 * A native <select>: the codebase has no Radix/shadcn select and styles native
 * ones (NeueAufgabeDialog, admin wizard).
 *
 * The options are the api's own allowed_transitions for that row, so the control
 * can only offer moves the state machine accepts. An admin holding
 * customer_status.override sees all statuses but inaktiv instead (the nightly run
 * sets inaktiv, P6), since that route bypasses the table. Read-only when the caller holds neither right, when the status is
 * 'inaktiv' (the api answers 422 for everyone, override included), or when there
 * is nowhere legal to go.
 *
 * Renders the shared StatusDropdown; this component decides the Kunden options and
 * read-only rules.
 */

import { StatusDropdown } from '../../../components/ui/StatusDropdown';
import { KUNDE_STATUS_META, KUNDE_STATUS_OPTIONS } from '../customer-status-meta';
import type { KundeStatus } from '../../entity-detail/types';

interface StatusSelectProps {
  status: KundeStatus;
  /** Rejects when the write fails; the caller does not need to handle the error. */
  onChange: (next: KundeStatus) => Promise<unknown>;
  /** The api's legal targets for this row. Never recomputed here (rule #2). */
  allowedTransitions: KundeStatus[];
  /** Holds customer_status.transition: may move along the legal path. */
  canTransition: boolean;
  /** Holds customer_status.override: may set ANY status, bypassing the table. */
  canOverride: boolean;
  className?: string;
  ariaLabel?: string;
}

export function StatusSelect({
  status,
  onChange,
  allowedTransitions,
  canTransition,
  canOverride,
  className,
  ariaLabel = 'Kundenstatus',
}: StatusSelectProps) {
  const meta = KUNDE_STATUS_META[status];
  // An override holder may pick anything; everyone else only the legal targets.
  // 'inaktiv' stays read-only for BOTH: the api refuses every transition out of
  // it, override included, so offering the control would promise a lie.
  // P6 (concept 10.1): inaktiv is set by the nightly run only, the api refuses it by hand and by
  // override, so it is never offered.
  const options = canOverride
    ? KUNDE_STATUS_OPTIONS.filter((o) => o.value !== 'inaktiv' || status === 'inaktiv')
    : [status, ...allowedTransitions].map((value) => ({
        value,
        label: KUNDE_STATUS_META[value].label,
      }));
  const readOnly =
    (!canTransition && !canOverride) || status === 'inaktiv' || options.length <= 1;

  return (
    <StatusDropdown<KundeStatus>
      value={status}
      options={options}
      onChange={onChange}
      current={meta}
      readOnly={readOnly}
      readOnlyTitle={
        status === 'inaktiv'
          ? 'Inaktive Kunden sind schreibgeschützt'
          : !canTransition && !canOverride
            ? 'Keine Berechtigung, den Status zu ändern'
            : 'Kein weiterer Statuswechsel möglich'
      }
      ariaLabel={ariaLabel}
      className={className}
    />
  );
}
